import { Pause, Play } from '@phosphor-icons/react';
import type { VoiceMessage } from '@sirocco/shared';
import { useEffect, useRef, useState, type PointerEvent } from 'react';
import { t } from '../../i18n';

/**
 * Un seul vocal a la fois.
 *
 * Sans cela, lancer un message pendant qu'un autre joue superpose deux
 * voix — et il n'y a aucun moyen d'arreter celui qu'on ne voit plus.
 * L'element courant se retient ici, hors de tout composant : deux bulles
 * eloignees dans le fil ne se connaissent pas.
 */
let playing: HTMLAudioElement | null = null;

/**
 * Comment lancer un vocal donne, depuis un autre.
 *
 * Une salve de vocaux s'ecoute d'une traite : quand l'un finit, il
 * appelle le suivant par son identifiant. Les bulles ne se connaissent
 * pas entre elles, d'ou ce petit annuaire — chacune s'y inscrit tant
 * qu'elle est a l'ecran.
 *
 * L'enchainement joue sans geste de l'utilisateur, ce que WebKit refuse
 * d'ordinaire : c'est Capacitor qui leve la regle cote natif
 * (`mediaTypesRequiringUserActionForPlayback = []`).
 */
const chain = new Map<string, () => void>();

/** Au-dela, l'appui n'est plus une lecture mais un deplacement dans l'onde. */
const SCRUB_MS = 280;

/**
 * La bulle d'un message vocal : lecture, silhouette, duree.
 *
 * La silhouette vient du message — elle a ete calculee une fois, a
 * l'envoi. La redecouper a l'affichage demanderait de decoder l'audio de
 * chaque bulle a l'ouverture du fil.
 */
export function VoicePlayer({
  id,
  voice,
  mine,
  nextId,
}: {
  /** Identifiant du message, pour l'annuaire d'enchainement. */
  id: string;
  voice: VoiceMessage;
  mine: boolean;
  /** Le vocal a enchainer, s'il y en a un juste apres dans la meme salve. */
  nextId: string | null;
}) {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [isPlaying, setPlaying] = useState(false);
  const [elapsedMs, setElapsedMs] = useState(0);
  /** Position visee pendant qu'on se promene dans l'onde, de 0 a 1. */
  const [scrub, setScrub] = useState<number | null>(null);

  const wave = useRef<HTMLSpanElement>(null);
  const holdTimer = useRef(0);
  /*
    L'avancee ne passe pas par React.

    Elle etait tiree de `timeupdate`, que les navigateurs n'emettent que
    quatre fois par seconde : les barres s'allumaient une a une, par
    paquets, et l'onde avancait par a-coups. Elle est maintenant lue a
    chaque image d'animation et posee directement sur le style — soixante
    fois par seconde, sans recomposer quarante barres a chaque fois.

    Le compteur, lui, reste un etat : il ne change qu'une fois par
    seconde, et un rendu par seconde ne coute rien.
  */
  const imageRef = useRef(0);
  const secondeRef = useRef(-1);
  /*
    Le suivant change au fil des rendus, et la lecture le relit au moment
    ou elle se termine — pas au moment ou l'element audio a ete construit.
  */
  const nextRef = useRef(nextId);
  nextRef.current = nextId;

  /*
    L'element audio n'est cree qu'au premier appui, pas au rendu : un fil
    de vingt vocaux instancierait vingt lecteurs — et sur iOS, chacun
    reserve une ressource au systeme.
  */
  function player(): HTMLAudioElement {
    if (!audioRef.current) {
      const audio = new Audio(voice.audioUrl);
      audio.preload = 'none';
      audio.addEventListener('ended', () => {
        setPlaying(false);
        setElapsedMs(0);
        poser(0);
        if (playing === audio) playing = null;
        // La salve continue toute seule ; le dernier s'arrete simplement.
        const after = nextRef.current;
        if (after) chain.get(after)?.();
      });
      audioRef.current = audio;
    }
    return audioRef.current;
  }

  /** Pose l'avancee sur l'element, de 0 a 1. Rien ne passe par React. */
  function poser(part: number) {
    wave.current?.style.setProperty('--joue', `${part * 100}%`);
  }

  /**
   * Suit la lecture, image par image.
   *
   * `currentTime` est lu a chaque passage plutot que calcule depuis un
   * horodatage de depart : une pause, un deplacement dans l'onde ou un
   * chargement qui bloque un instant decaleraient le calcul, et l'onde
   * finirait ailleurs que la voix.
   */
  function suivre() {
    const audio = audioRef.current;
    if (!audio) return;
    imageRef.current = requestAnimationFrame(suivre);
    if (audio.paused) return;

    const ms = audio.currentTime * 1000;
    poser(Math.min(1, ms / Math.max(1, voice.durationMs)));

    // Le compteur ne bouge qu'au changement de seconde affichee.
    const seconde = Math.round((voice.durationMs - ms) / 1000);
    if (seconde !== secondeRef.current) {
      secondeRef.current = seconde;
      setElapsedMs(ms);
    }
  }

  /** Lance depuis le debut, ou depuis l'endroit demande. */
  function start(fromRatio?: number) {
    const audio = player();
    if (playing && playing !== audio) playing.pause();
    playing = audio;

    if (fromRatio !== undefined) {
      audio.currentTime = (fromRatio * voice.durationMs) / 1000;
      setElapsedMs(fromRatio * voice.durationMs);
      poser(fromRatio);
    }
    void audio.play().then(() => {
      setPlaying(true);
      cancelAnimationFrame(imageRef.current);
      imageRef.current = requestAnimationFrame(suivre);
    });
  }

  // Tant que la bulle est a l'ecran, elle est joignable par la precedente.
  useEffect(() => {
    chain.set(id, () => start());
    return () => {
      chain.delete(id);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // Quitter la discussion coupe le son : il ne suit pas d'un ecran a l'autre.
  useEffect(() => {
    return () => {
      cancelAnimationFrame(imageRef.current);
      const audio = audioRef.current;
      if (!audio) return;
      audio.pause();
      if (playing === audio) playing = null;
    };
  }, []);

  function toggle() {
    const audio = player();
    if (isPlaying) {
      audio.pause();
      cancelAnimationFrame(imageRef.current);
      setPlaying(false);
      return;
    }
    start();
  }

  /** Ou se trouve le doigt dans l'onde, de 0 a 1. */
  function ratioAt(clientX: number): number {
    const box = wave.current?.getBoundingClientRect();
    if (!box || box.width === 0) return 0;
    return Math.min(1, Math.max(0, (clientX - box.left) / box.width));
  }

  /*
    L'onde repond a deux gestes.

    Un appui bref lance ou met en pause — c'est le geste evident, et il
    n'y a pas de raison que seule l'icone y reponde. Un appui maintenu
    ouvre le deplacement : on se promene dans le message, et c'est en
    lachant qu'on lance l'endroit choisi.

    L'evenement s'arrete ici : sans cela, la bulle qui entoure l'onde
    ouvrirait sa feuille d'actions par-dessus le deplacement. Le reste de
    la bulle continue de la proposer.
  */
  function onWaveDown(event: PointerEvent<HTMLSpanElement>) {
    event.stopPropagation();
    event.currentTarget.setPointerCapture(event.pointerId);
    const at = ratioAt(event.clientX);
    holdTimer.current = window.setTimeout(() => {
      setScrub(at);
      poser(at);
    }, SCRUB_MS);
  }

  function onWaveMove(event: PointerEvent<HTMLSpanElement>) {
    if (scrub === null) return;
    event.stopPropagation();
    const at = ratioAt(event.clientX);
    setScrub(at);
    poser(at);
  }

  function onWaveUp(event: PointerEvent<HTMLSpanElement>) {
    event.stopPropagation();
    window.clearTimeout(holdTimer.current);

    if (scrub === null) {
      toggle();
      return;
    }

    start(scrub);
    setScrub(null);
  }

  /* Les barres sont les memes des deux cotes : dessinees une fois, et
     posees deux. */
  const barres = voice.peaks.map((peak, index) => (
    <span key={index} className="voice__bar" style={{ height: `${Math.max(12, peak)}%` }} />
  ));

  const total = Math.max(1, voice.durationMs);
  /* Pour le compteur seulement : l'onde, elle, est posee image par image. */
  const played = isPlaying || elapsedMs > 0 ? Math.min(1, elapsedMs / total) : 0;
  const progress = scrub ?? played;
  // Ce qui reste a ecouter, comme sur les messageries : le compteur
  // descend, et il vaut la duree entiere tant qu'on n'a pas commence.
  const shown = progress > 0 ? total - progress * total : total;

  return (
    <span className={`voice${mine ? ' voice--mine' : ''}`}>
      <button
        type="button"
        className="voice__play"
        aria-label={isPlaying ? t.composants.vocal.pause : t.composants.vocal.ecouter}
        onClick={toggle}
      >
        {isPlaying ? <Pause size={22} weight="fill" /> : <Play size={22} weight="fill" />}
      </button>

      {/*
        La silhouette est decorative pour un lecteur d'ecran : la duree
        juste a cote dit deja ce qu'il y a a savoir, et quarante barres
        lues une a une n'aideraient personne. Le bouton reste le chemin
        accessible ; l'onde n'est qu'un raccourci pour le doigt.
      */}
      <span
        ref={wave}
        className={`voice__wave${scrub !== null ? ' voice__wave--scrubbing' : ''}`}
        aria-hidden="true"
        onPointerDown={onWaveDown}
        onPointerMove={onWaveMove}
        onPointerUp={onWaveUp}
        onPointerCancel={onWaveUp}
      >
        {/*
          Deux rangees identiques, l'une par-dessus l'autre : celle du
          dessous pour ce qui reste, celle du dessus pour ce qui est joue,
          rognee a l'avancee. C'est ce qui permet a la limite de tomber au
          milieu d'une barre — allumer les barres une a une donnait, sur
          quarante barres, un pas de deux cent cinquante millisecondes.

          Le rognage passe par `clip-path` et non par une largeur : une
          largeur ferait remonter les barres a la mise en page a chaque
          image, quand un rognage se joue au compositeur.
        */}
        <span className="voice__bars">{barres}</span>
        <span className="voice__bars voice__bars--played">{barres}</span>
      </span>

      <span className="voice__time">{clock(shown)}</span>
    </span>
  );
}

/** Les barres d'un apercu : moitie moins que la bulle, a son echelle. */
const BARRES_APERCU = 20;

/**
 * Un vocal cite : son allure, et pas son nom.
 *
 * « Message vocal » au-dessus d'une reponse disait de quoi il s'agissait
 * sans dire lequel ; dans une salve de trois vocaux, on ne savait plus a
 * qui l'on repondait. L'apercu reprend ce qui distingue un vocal d'un
 * autre, sa silhouette et sa duree, sous la forme qu'on connait deja dans
 * le fil. Il ne joue rien : la citation mene au message, c'est la qu'on
 * l'ecoute.
 *
 * La silhouette est reduite en gardant le plus haut de chaque paire : une
 * moyenne aplatirait justement les pointes qui la rendent reconnaissable.
 */
export function VoiceApercu({ voice }: { voice: VoiceMessage }) {
  const pas = Math.max(1, Math.ceil(voice.peaks.length / BARRES_APERCU));
  const barres: number[] = [];
  for (let i = 0; i < voice.peaks.length; i += pas) {
    barres.push(Math.max(...voice.peaks.slice(i, i + pas)));
  }

  return (
    <span className="voice-apercu" role="img" aria-label={t.composants.vocal.apercu(clock(voice.durationMs))}>
      <Play size={13} weight="fill" className="voice-apercu__play" aria-hidden="true" />
      <span className="voice-apercu__bars" aria-hidden="true">
        {barres.map((pic, index) => (
          <span
            key={index}
            className="voice-apercu__bar"
            style={{ height: `${Math.max(18, pic)}%` }}
          />
        ))}
      </span>
      <span className="voice-apercu__time" aria-hidden="true">
        {clock(voice.durationMs)}
      </span>
    </span>
  );
}

/** Millisecondes -> "0:07". */
function clock(ms: number): string {
  const seconds = Math.max(0, Math.round(ms / 1000));
  return `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, '0')}`;
}
